const MS_PER_DAY = 86_400_000;
/**
 * Window size in days that counts as "recent". Bundles deployed within this
 * window return `true`; older ones return `false`; unknown-age inputs return
 * `undefined`.
 *
 * 7 days intentionally matches typical tab lifetimes and the weekly deploy
 * cadence. The threshold lives with the code that knows about the Vite
 * plugin's `min-version` semantics rather than with the alerting layer, so
 * Terraform monitors stay decoupled from the decision.
 */
const RECENT_BUILD_WINDOW_DAYS = 7;
const RECENT_BUILD_WINDOW_MS = RECENT_BUILD_WINDOW_DAYS * MS_PER_DAY;
/**
 * Returns whether the client's build is within the recent-build window.
 *
 * Output:
 * - `true`  — build timestamp is present and within the window (or in the
 *   future, treated as recent for clock-skewed clients).
 * - `false` — build timestamp is present but past the window.
 * - `undefined` — no (or invalid) build timestamp; caller should emit
 *   `is_recent_build: undefined` so Datadog receives the property with no
 *   value and the absent-facet filter excludes the session from monitors.
 *
 * Use for a single low-cardinality Datadog facet (`@is_recent_build`) on
 * every RUM / log event from an SPA. Monitors filter with
 * `@is_recent_build:true`, so pre-instrumented / ancient bundles (which
 * don't call this at all) and stale bundles (which return `false`) are both
 * excluded.
 *
 * Callers own the DOM (or cookie / SSR / bootstrap-JSON) read and the
 * numeric coercion — typically in the SPA's bootstrap config.
 *
 * @param buildTimestampMs Unix milliseconds at build time. Pass `undefined`
 *   (or any non-positive / non-finite number) to signal unknown.
 * @param nowMs Injected for deterministic testing; defaults to `Date.now()`.
 */
export function isRecentBuild(buildTimestampMs, nowMs = Date.now()) {
    if (buildTimestampMs === undefined ||
        !Number.isFinite(buildTimestampMs) ||
        buildTimestampMs <= 0) {
        return undefined;
    }
    return nowMs - buildTimestampMs <= RECENT_BUILD_WINDOW_MS;
}
//# sourceMappingURL=isRecentBuild.js.map